import React, { useState, useEffect } from 'react';
import { Typography, Grid, Chip, Select, Option } from '@mui/joy';
import { Card, Button, Table, Input, Alert } from '@components/ui';
import { securityService, SecurityScan, Vulnerability } from '@services/security.service';
import {
  MagnifyingGlassIcon,
  ShieldExclamationIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';

const severityColors = {
  critical: { color: 'danger' as const, icon: ShieldExclamationIcon, bgColor: 'bg-red-100' },
  high: { color: 'warning' as const, icon: ExclamationTriangleIcon, bgColor: 'bg-orange-100' },
  medium: { color: 'primary' as const, icon: InformationCircleIcon, bgColor: 'bg-blue-100' },
  low: { color: 'neutral' as const, icon: InformationCircleIcon, bgColor: 'bg-gray-100' },
};

export const VulnerabilityScan: React.FC = () => {
  const [targetUrl, setTargetUrl] = useState('');
  const [scanning, setScanning] = useState(false);
  const [activeScans, setActiveScans] = useState<SecurityScan[]>([]);
  const [vulnerabilities, setVulnerabilities] = useState<Vulnerability[]>([]);
  const [selectedSeverity, setSelectedSeverity] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchVulnerabilities();
    fetchActiveScans();
  }, [selectedSeverity, selectedStatus]);

  const fetchVulnerabilities = async () => {
    setLoading(true);
    try {
      const filters: any = {};
      if (selectedSeverity !== 'all') filters.severity = selectedSeverity;
      if (selectedStatus !== 'all') filters.status = selectedStatus;
      
      const data = await securityService.getVulnerabilities(filters);
      setVulnerabilities(data);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch vulnerabilities');
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveScans = async () => {
    try {
      const scans = await securityService.getScans({ 
        type: 'vulnerability',
        status: 'in-progress'
      });
      setActiveScans(scans);
    } catch (err: any) {
      console.error('Failed to fetch active scans:', err);
    }
  };

  const startScan = async () => {
    if (!targetUrl.trim()) {
      setError('Please enter a target URL');
      return;
    }

    setScanning(true);
    setError(null);

    try {
      await securityService.startVulnerabilityScan(targetUrl);
      setTargetUrl('');
      await fetchActiveScans();
    } catch (err: any) {
      setError(err.message || 'Failed to start vulnerability scan');
    } finally {
      setScanning(false);
    }
  };

  const updateVulnerabilityStatus = async (id: string, status: Vulnerability['status']) => {
    try {
      await securityService.updateVulnerabilityStatus(id, status);
      await fetchVulnerabilities();
    } catch (err: any) {
      setError(err.message || 'Failed to update vulnerability status');
    }
  };

  const vulnerabilityColumns = [
    { 
      key: 'severity', 
      header: 'Severity', 
      width: '10%',
      render: (value: string) => {
        const config = severityColors[value as keyof typeof severityColors];
        return (
          <Chip size="sm" color={config.color} variant="solid">
            {value.toUpperCase()}
          </Chip>
        );
      }
    },
    { 
      key: 'title', 
      header: 'Vulnerability', 
      width: '25%',
      render: (value: string, row: Vulnerability) => (
        <div>
          <div className="font-medium">{value}</div>
          {row.cveId && (
            <div className="text-xs text-gray-500">{row.cveId}</div>
          )}
        </div>
      )
    },
    { key: 'affectedComponent', header: 'Component', width: '20%' },
    { 
      key: 'status', 
      header: 'Status', 
      width: '15%',
      render: (value: string) => {
        const colors: Record<string, any> = {
          open: 'danger',
          'in-progress': 'warning',
          resolved: 'success',
          'false-positive': 'neutral'
        };
        return (
          <Chip size="sm" color={colors[value] || 'neutral'} variant="soft">
            {value.replace('-', ' ')}
          </Chip>
        );
      }
    },
    { 
      key: 'discovered', 
      header: 'Discovered', 
      width: '15%',
      render: (value: Date) => new Date(value).toLocaleDateString()
    },
    {
      key: 'id',
      header: 'Actions',
      width: '15%',
      render: (_: string, row: Vulnerability) => (
        <Select
          size="sm"
          placeholder="Update Status"
          onChange={(_, value) => value && updateVulnerabilityStatus(row.id, value as Vulnerability['status'])}
        >
          <Option value="open">Open</Option>
          <Option value="in-progress">In Progress</Option>
          <Option value="resolved">Resolved</Option>
          <Option value="false-positive">False Positive</Option>
        </Select>
      )
    },
  ];

  const scanColumns = [
    { key: 'targetUrl', header: 'Target', width: '30%' },
    { 
      key: 'status', 
      header: 'Status', 
      width: '15%',
      render: (value: string) => (
        <Chip size="sm" color="primary" variant="soft">
          {value}
        </Chip>
      )
    },
    { 
      key: 'startTime', 
      header: 'Started', 
      width: '25%',
      render: (value: Date) => new Date(value).toLocaleString()
    },
    {
      key: 'id',
      header: 'Progress',
      width: '30%',
      render: () => (
        <div className="flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-500" />
          <span className="text-sm">Scanning...</span>
        </div>
      )
    },
  ];

  const severityStats = vulnerabilities.reduce((acc, vuln) => {
    acc[vuln.severity] = (acc[vuln.severity] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Typography level="h2" className="font-bold text-gray-900 dark:text-white">
          Vulnerability Scanner
        </Typography>
        <Typography level="body-md" className="text-gray-600 dark:text-gray-400 mt-1">
          Scan for security vulnerabilities and manage findings
        </Typography>
      </div>

      {error && (
        <Alert color="danger" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Start New Scan */}
      <Card className="p-6">
        <Typography level="h4" className="font-semibold mb-4">
          Start New Vulnerability Scan
        </Typography>
        <div className="flex gap-3">
          <Input
            placeholder="https://example.com"
            value={targetUrl}
            onChange={(e) => setTargetUrl(e.target.value)}
            className="flex-1"
            startDecorator={<MagnifyingGlassIcon className="w-4 h-4" />}
          />
          <Button
            onClick={startScan}
            loading={scanning}
            startDecorator={<MagnifyingGlassIcon className="w-4 h-4" />}
          >
            Start Scan
          </Button>
        </div>
      </Card>

      {/* Vulnerability Statistics */}
      <Grid container spacing={3}>
        {Object.entries(severityColors).map(([severity, config]) => {
          const Icon = config.icon;
          const count = severityStats[severity] || 0;
          return (
            <Grid key={severity} xs={12} sm={6} md={3}>
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <Typography level="body-sm" className="text-gray-600 dark:text-gray-400 capitalize">
                      {severity}
                    </Typography>
                    <Typography level="h3" className="font-bold">
                      {count}
                    </Typography>
                  </div>
                  <div className={`p-3 rounded-lg ${config.bgColor}`}>
                    <Icon className="w-6 h-6 text-gray-600" />
                  </div>
                </div>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Active Scans */}
      {activeScans.length > 0 && (
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-4">
            <ClockIcon className="w-5 h-5 text-blue-600" />
            <Typography level="h4" className="font-semibold">
              Active Scans
            </Typography>
          </div>
          <Table
            columns={scanColumns}
            data={activeScans}
            striped
          />
        </Card>
      )}

      {/* Vulnerabilities */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <Typography level="h4" className="font-semibold">
            Vulnerability Findings
          </Typography>
          <div className="flex gap-3">
            <Select
              size="sm"
              placeholder="Filter by Severity"
              value={selectedSeverity}
              onChange={(_, value) => setSelectedSeverity(value as string)}
            >
              <Option value="all">All Severities</Option>
              <Option value="critical">Critical</Option>
              <Option value="high">High</Option>
              <Option value="medium">Medium</Option>
              <Option value="low">Low</Option>
            </Select>
            <Select
              size="sm"
              placeholder="Filter by Status"
              value={selectedStatus}
              onChange={(_, value) => setSelectedStatus(value as string)}
            >
              <Option value="all">All Statuses</Option>
              <Option value="open">Open</Option>
              <Option value="in-progress">In Progress</Option>
              <Option value="resolved">Resolved</Option>
              <Option value="false-positive">False Positive</Option>
            </Select>
          </div>
        </div>
        
        <Table
          columns={vulnerabilityColumns}
          data={vulnerabilities}
          striped
          loading={loading}
          emptyMessage="No vulnerabilities found"
        />
      </Card>
    </div>
  );
};